import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Loader2, Plus, Trash2, Percent } from "lucide-react";
import { invalidateCustomerLevelsCache } from "@/hooks/useCustomerLevels";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MAX_LEVELS = 6;

export default function CustomerLevels() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // levels is an array of { name, discount } with Standard fixed first.
  const [levels, setLevels] = useState([{ name: "Standard", discount: 0 }]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/customer-levels`);
        const list = (res.data?.levels || []).map((l) => ({
          name: l.name,
          discount: Number(l.discount) || 0,
        }));
        setLevels(list.length ? list : [{ name: "Standard", discount: 0 }]);
      } catch {
        toast.error("Couldn't load customer levels");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateDiscount = (idx, val) => {
    setLevels((prev) => prev.map((l, i) => (i === idx ? { ...l, discount: val } : l)));
  };

  const addLevel = () => {
    setLevels((prev) => {
      const nextNum = prev.filter((l) => l.name !== "Standard").length + 1;
      if (nextNum > MAX_LEVELS) {
        toast.error(`Max ${MAX_LEVELS} levels supported`);
        return prev;
      }
      return [...prev, { name: `Level ${nextNum}`, discount: 0 }];
    });
  };

  const removeLevel = (idx) => {
    setLevels((prev) => {
      if (prev[idx]?.name === "Standard") return prev;
      const kept = prev.filter((_, i) => i !== idx);
      // Renumber Level 1..N so numbering stays contiguous.
      let n = 1;
      return kept.map((l) => (l.name === "Standard" ? l : { ...l, name: `Level ${n++}` }));
    });
  };

  const save = async () => {
    // Validate.
    const map = {};
    for (const l of levels) {
      const rate = parseFloat(l.discount);
      if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
        toast.error(`Invalid rate for ${l.name}`);
        return;
      }
      map[l.name] = rate;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/customer-levels`, { levels: map });
      const returned = (res.data?.levels || []).map((l) => ({
        name: l.name,
        discount: Number(l.discount) || 0,
      }));
      setLevels(returned);
      invalidateCustomerLevelsCache();
      toast.success("Customer levels updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4 p-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Percent className="h-5 w-5" /> Customer Levels
          </CardTitle>
          <CardDescription>
            Each customer can be assigned a level. The level&rsquo;s discount is applied automatically at
            checkout on eligible items. Standard is always 0% (no automatic discount).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {levels.map((lvl, idx) => (
            <div key={lvl.name} className="flex items-center gap-3">
              <Label className="w-24 font-medium">{lvl.name}</Label>
              <div className="relative flex-1 max-w-[180px]">
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={lvl.discount}
                  disabled={lvl.name === "Standard"}
                  onChange={(e) => updateDiscount(idx, e.target.value)}
                  className="pr-8"
                  data-testid={`level-rate-${lvl.name.replace(/\s+/g, "-")}`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
              </div>
              {lvl.name !== "Standard" && idx === levels.length - 1 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600"
                  onClick={() => removeLevel(idx)}
                  data-testid={`level-remove-${lvl.name.replace(/\s+/g, "-")}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}

          {levels.filter((l) => l.name !== "Standard").length < MAX_LEVELS && (
            <Button
              variant="outline"
              size="sm"
              onClick={addLevel}
              className="mt-2"
              data-testid="level-add-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add another level
            </Button>
          )}

          <div className="pt-4 border-t">
            <Button onClick={save} disabled={saving} data-testid="levels-save-btn">
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                <><Save className="h-4 w-4 mr-2" /> Save levels</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4 text-sm text-blue-900 space-y-1">
          <p className="font-semibold">How it works</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Level discounts stack with any per-customer <em>Additional discount %</em>.</li>
            <li>Products marked as <em>promotion-only</em> or <em>staff-priced</em> ignore level discounts.</li>
            <li>Existing customers keep their assigned level — only the rate changes.</li>
            <li>Max 6 levels. Level numbers stay contiguous (1, 2, 3…).</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
